import { API_BASE_URL } from '../Utils/constants';
import { get, post, del } from './httpClient';
import { authFetch } from './authFetch';
import { spaceOwnerQueryParams } from './spaceMembersService';
import { noteMarketComparisonFeature } from '../MarketComparison/marketComparisonConfig';
import {
  normalizeCommercialRollup,
  normalizeCommercialSummary,
  normalizeLotCommercial,
} from '../Leads/commercialSummary';

// El Bearer (renovado, sin `Bearer null`, reintento ante 401) lo pone `authFetch`.
const getHeaders = () => ({
  'Content-Type': 'application/json',
});

const parseJsonResponse = async (response) => {
  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }
  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

/**
 * Crea o actualiza una unidad funcional; el backend resuelve el upsert por `user_lot_id`.
 *
 * @param {object} data - Payload de la unidad.
 * @param {{ ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `owner_user_id` en el body (invitado en espacio ajeno):
 *        alta = UF del owner creada por el invitado (`is_public` forzado false);
 *        update de una UF que creó → ruta completa; otra UF → solo copy
 *        comercial + `stock_status`. Listas ajenas → 403 `{ foreign_list_ids }`.
 * @returns {Promise<object>} Envelope de `httpClient`; la unidad guardada viene en `data`.
 */
export const createUserLot = (data, { ownerUserId } = {}) => post('/createuserlot', {
  body: { ...data, ...spaceOwnerQueryParams(ownerUserId) },
});

export const getUfAttributeSchema = async (buildingType) => {
  const params = buildingType
    ? `?building_type=${encodeURIComponent(buildingType)}`
    : '';
  const response = await authFetch(`${API_BASE_URL}/getufattributeschema${params}`, {
    headers: getHeaders(),
  });
  return response.json();
};

/**
 * `GET /getmyrecords`: una card plana por lote / UF / parcela CAD (contrato:
 * `argenlotapi/docs/frontend/FRONTEND_RECORDS.md`). Solo para la fuente `api`
 * de las cards por registro (`Map/utils/recordsSource.js`).
 *
 * @param {{
 *   kind?: string|string[],      // lote, uf, cad, marker (default API: lote,uf,cad)
 *   listId?: string,             // solo lote y uf
 *   sort?: 'updated_desc'|'created_desc'|'name_asc'|'price_asc'|'price_desc',
 *   limit?: number,              // 1–200 (default API 50)
 *   cursor?: string|null,        // next_cursor de la página anterior (mismo sort)
 *   include?: string|string[],   // default commercial_summary,shape
 *   view?: 'list'|'map',
 *   ownerUserId?: string|null,   // espejo de espacio (editor)
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<{ ok: boolean, status: number, data: object[], total: number|null,
 *   totals_by_kind: object|null, next_cursor: string|null, truncated: boolean,
 *   sort: string|null, error?: string, code?: string, field?: string }>}
 */
export const getMyRecords = async ({
  kind,
  listId,
  sort,
  limit,
  cursor,
  include = ['commercial_summary', 'shape'],
  view,
  ownerUserId,
  signal,
} = {}) => {
  const params = new URLSearchParams();
  const csv = (value) => (Array.isArray(value) ? value.filter(Boolean).join(',') : String(value || '').trim());
  if (csv(kind)) params.set('kind', csv(kind));
  if (listId) params.set('list_id', String(listId));
  if (sort) params.set('sort', sort);
  if (limit != null) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (csv(include)) params.set('include', csv(include));
  if (view) params.set('view', view);
  if (ownerUserId) params.set('owner_user_id', String(ownerUserId).trim());
  const qs = params.toString() ? `?${params.toString()}` : '';

  const response = await authFetch(`${API_BASE_URL}/getmyrecords${qs}`, {
    headers: getHeaders(),
    signal,
  });
  let json = {};
  try {
    json = await response.json();
  } catch {
    json = {};
  }
  const data = Array.isArray(json?.data) ? json.data : [];
  // `features.market_comparison`: prende / apaga la tarjeta «Precio vs. mercado».
  if (response.ok) noteMarketComparisonFeature(json?.features);
  return {
    ok: response.ok,
    status: response.status,
    data: data.map((card) => (
      card?.commercial_summary
        ? { ...card, commercial_summary: normalizeCommercialSummary(card.commercial_summary) }
        : card
    )),
    total: typeof json?.total === 'number' ? json.total : null,
    totals_by_kind: json?.totals_by_kind && typeof json.totals_by_kind === 'object' ? json.totals_by_kind : null,
    next_cursor: json?.next_cursor || null,
    truncated: json?.truncated === true,
    // Ronda 2: lotes distintos para «N lotes» (null si no viene).
    distinct_lot_count: typeof json?.distinct_lot_count === 'number' ? json.distinct_lot_count : null,
    sort: json?.sort || null,
    ...(response.ok ? {} : { error: json?.error, code: json?.code, field: json?.field }),
  };
};

/**
 * Todas las páginas de `getmyrecords` (sigue `next_cursor`). Las cards por registro
 * filtran y ordenan en el Front, así que necesitan el universo completo, como hoy
 * con `getmylots`. Corta en `maxPages` (200 × 25 = 5000 registros).
 *
 * `onPage(page, { data, pageIndex, total, done })` se llama al llegar cada página
 * (con lo acumulado hasta ahí): el panel pinta la primera sin esperar al resto.
 * Las páginas se piden en serie (el cursor de una sale de la anterior).
 * @param {Parameters<typeof getMyRecords>[0] & { maxPages?: number, onPage?: Function }} [options]
 * @returns {Promise<{ ok: boolean, status: number, data: object[], total: number|null, totals_by_kind: object|null, truncated: boolean, pages: number }>}
 */
export const getAllMyRecords = async ({ maxPages = 25, onPage, ...options } = {}) => {
  const data = [];
  let cursor = null;
  let first = null;
  for (let page = 0; page < maxPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await getMyRecords({ limit: 200, ...options, cursor });
    if (!res.ok) return { ...res, data, pages: page };
    if (!first) first = res;
    data.push(...res.data);
    cursor = res.next_cursor;
    const done = !cursor || page === maxPages - 1;
    if (typeof onPage === 'function') {
      onPage(res, { data: data.slice(), pageIndex: page, total: first.total, done });
    }
    if (!cursor) {
      return {
        ok: true,
        status: res.status,
        data,
        total: first.total,
        totals_by_kind: first.totals_by_kind,
        distinct_lot_count: first.distinct_lot_count,
        truncated: false,
        pages: page + 1,
      };
    }
  }
  return {
    ok: true,
    status: 200,
    data,
    total: first?.total ?? null,
    totals_by_kind: first?.totals_by_kind ?? null,
    distinct_lot_count: first?.distinct_lot_count ?? null,
    truncated: true,
    pages: maxPages,
  };
};

/**
 * @param {string|{ listId?: string, favoritesOnly?: boolean, includeFilterAttributes?: boolean, includePrivateAttributes?: boolean, includeCommercialSummary?: boolean, includeLotContentSummary?: boolean, include?: string, ownerUserId?: string, signal?: AbortSignal }} [listIdOrOptions]
 *        UUID de lista, u opciones. Compat: `getMyLots(listId, signal)`.
 *        `include` pisa los booleanos: p. ej. `'filter_attributes,private_attributes'`.
 *        `ownerUserId` → `?owner_user_id=` (espejo de espacio compartido; ver espacios-invite).
 * @param {AbortSignal} [signal]
 * @see docs/maps/parcel-filters-scope-backend-spec.md — `include=filter_attributes,private_attributes`
 * @see docs/maps/espacios-invite.md
 */
export const getMyLots = async (listIdOrOptions, signal) => {
  let listId;
  let favoritesOnly = false;
  let includeFilterAttributes = false;
  let includePrivateAttributes = false;
  let includeCommercialSummary = false;
  let includeLotContentSummary = false;
  let includeOverride = null;
  let ownerUserId = null;
  let abortSignal = signal;

  if (
    listIdOrOptions
    && typeof listIdOrOptions === 'object'
    && !Array.isArray(listIdOrOptions)
  ) {
    listId = listIdOrOptions.listId;
    favoritesOnly = Boolean(listIdOrOptions.favoritesOnly);
    includeFilterAttributes = Boolean(listIdOrOptions.includeFilterAttributes);
    includePrivateAttributes = Boolean(listIdOrOptions.includePrivateAttributes);
    includeCommercialSummary = Boolean(listIdOrOptions.includeCommercialSummary);
    includeLotContentSummary = Boolean(listIdOrOptions.includeLotContentSummary);
    includeOverride = typeof listIdOrOptions.include === 'string' && listIdOrOptions.include.trim()
      ? listIdOrOptions.include.trim()
      : null;
    ownerUserId = listIdOrOptions.ownerUserId
      ? String(listIdOrOptions.ownerUserId).trim()
      : null;
    abortSignal = listIdOrOptions.signal ?? signal;
  } else {
    listId = listIdOrOptions;
  }

  const params = new URLSearchParams();
  if (listId) params.set('list_id', listId);
  if (favoritesOnly) params.set('favorites_only', 'true');
  if (ownerUserId) params.set('owner_user_id', ownerUserId);
  const include = includeOverride || [
    includeFilterAttributes ? 'filter_attributes' : null,
    includePrivateAttributes ? 'private_attributes' : null,
    // Opt-in: +~10% de latencia. Solo donde se muestra (Registros, detalle de lista).
    includeCommercialSummary ? 'commercial_summary' : null,
    // `lot_content` en cards con contenido. Solo cards de Registros y detalle de lista.
    includeLotContentSummary ? 'lot_content_summary' : null,
  ].filter(Boolean).join(',') || null;
  if (include) params.set('include', include);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const response = await authFetch(`${API_BASE_URL}/getmylots${qs}`, {
    headers: getHeaders(),
    signal: abortSignal,
  });
  const json = await response.json();
  if (include && include.includes('commercial_summary') && Array.isArray(json?.data)) {
    return { ...json, data: json.data.map(normalizeLotCommercial) };
  }
  return json;
};

/**
 * @param {string} lotId - UUID catastral del lote (fallback)
 * @param {string} [userLotId] - UUID de la unidad (preferido si está disponible)
 * @param {{ ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (UF del owner en espacio ajeno, F1).
 *        Ajeno: 403 si no es miembro, 404 si la UF no es del owner (sin `data`).
 */
export const getUserLotDetail = async (lotId, userLotId, { ownerUserId, includeCommercialSummary = false } = {}) => {
  const params = new URLSearchParams({
    ...(userLotId ? { user_lot_id: userLotId } : { lot_id: lotId }),
    ...spaceOwnerQueryParams(ownerUserId),
    // Resumen comercial (dueño / editor; nunca share). Sin él, respuesta de siempre.
    ...(includeCommercialSummary ? { include: 'commercial_summary' } : {}),
  });
  // Sin `Content-Type` (GET simple, como antes); el Bearer lo pone `authFetch`.
  const response = await authFetch(`${API_BASE_URL}/getuserlotdetail?${params}`);
  const json = await response.json();
  if (includeCommercialSummary && json?.data && typeof json.data === 'object'
    && 'commercial_summary' in json.data) {
    return { ...json, data: { ...json.data, commercial_summary: normalizeCommercialSummary(json.data.commercial_summary) } };
  }
  return json;
};

/** @param {string} userLotId - UUID de la unidad a eliminar */
/**
 * DELETE /deleteuserlot. `ownerUserId` → `&owner_user_id=` (espacio ajeno, mig 082):
 * solo UF que creó el propio invitado (`can_delete`); si no → 404.
 * @param {string} userLotId
 * @param {{ ownerUserId?: string|null }} [options]
 */
export const deleteUserLot = async (userLotId, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    user_lot_id: userLotId,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(`${API_BASE_URL}/deleteuserlot?${params}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

export const updateUserLotVisibility = async (lotId, isPublic) => {
  const response = await authFetch(`${API_BASE_URL}/createuserlot`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ lot_id: lotId, is_public: isPublic }),
  });
  return response.json();
};

/**
 * GET /getuserlotlists. Opcional `ownerUserId` → `?owner_user_id=` (espacio ajeno).
 * Compat: `getUserLotLists(signal)`.
 *
 * @param {AbortSignal|{ signal?: AbortSignal, ownerUserId?: string }} [signalOrOptions]
 * @see docs/maps/espacios-invite.md
 */
export const getUserLotLists = async (signalOrOptions) => {
  let signal;
  let ownerUserId = null;
  if (
    signalOrOptions
    && typeof signalOrOptions === 'object'
    && typeof signalOrOptions.aborted !== 'boolean'
  ) {
    signal = signalOrOptions.signal;
    ownerUserId = signalOrOptions.ownerUserId
      ? String(signalOrOptions.ownerUserId).trim()
      : null;
  } else {
    signal = signalOrOptions;
  }
  const params = new URLSearchParams();
  if (ownerUserId) params.set('owner_user_id', ownerUserId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await authFetch(`${API_BASE_URL}/getuserlotlists${qs}`, {
    headers: getHeaders(),
    signal,
  });
  return response.json();
};

/**
 * POST /createuserlotlist. `ownerUserId` → `owner_user_id` en el body: la lista
 * nace en el espacio del owner (F2; la API ignora `is_public` del invitado).
 */
export const createUserLotList = async ({ name, is_public, lot_ids, user_lot_ids, ownerUserId }) => {
  const body = {
    name,
    ...(typeof is_public === 'boolean' ? { is_public } : {}),
    ...spaceOwnerQueryParams(ownerUserId),
  };
  if (Array.isArray(lot_ids)) body.lot_ids = lot_ids.filter(Boolean);
  if (Array.isArray(user_lot_ids)) body.user_lot_ids = user_lot_ids.filter(Boolean);

  const response = await authFetch(`${API_BASE_URL}/createuserlotlist`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return parseJsonResponse(response);
};

/**
 * POST /updateuserlotlist. `ownerUserId` → `owner_user_id` en el body (F2):
 * el invitado solo renombra y agrega (`lot_ids` / `user_lot_ids` ⊇ actual;
 * si falta algo → 403 con `removed`). `is_public` del invitado → 403.
 */
export const updateUserLotList = async ({
  list_id: listId,
  name,
  is_public,
  lot_ids,
  user_lot_ids,
  ownerUserId,
}) => {
  const body = {
    list_id: listId,
    ...(typeof name === 'string' ? { name } : {}),
    ...(typeof is_public === 'boolean' ? { is_public } : {}),
    ...spaceOwnerQueryParams(ownerUserId),
  };
  if (Array.isArray(lot_ids)) body.lot_ids = lot_ids.filter(Boolean);
  if (Array.isArray(user_lot_ids)) body.user_lot_ids = user_lot_ids.filter(Boolean);

  const response = await authFetch(`${API_BASE_URL}/updateuserlotlist`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return parseJsonResponse(response);
};

export const deleteUserLotList = async (listId) => {
  const response = await authFetch(
    `${API_BASE_URL}/deleteuserlotlist?list_id=${encodeURIComponent(listId)}`,
    { method: 'DELETE', headers: getHeaders() }
  );
  return response.json();
};

/**
 * @param {string} userLotId - UUID de la unidad funcional
 * @param {{ ownerUserId?: string|null }} [options] `?owner_user_id=` (F2; mismo owner en el save)
 */
export const getUserLotPresignedUrl = async (userLotId, filename, contentType, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    user_lot_id: userLotId,
    filename,
    contentType,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(`${API_BASE_URL}/getuserlotpresignedurl?${params}`, {
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/**
 * @param {string} userLotId - UUID de la unidad funcional
 * @param {{ ownerUserId?: string|null }} [options] `?owner_user_id=` (F2; mismo owner que el presign)
 */
export const saveUserLotImage = async (userLotId, imageUrl, s3Key, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    user_lot_id: userLotId,
    image_url: imageUrl,
    s3_key: s3Key,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(`${API_BASE_URL}/saveuserlotimage?${params}`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/**
 * @param {string} imageId
 * @param {{ ownerUserId?: string|null }} [options] `&owner_user_id=` (UF creada por el invitado, mig 082)
 */
export const deleteUserLotImage = async (imageId, { ownerUserId } = {}) => {
  const params = new URLSearchParams({ image_id: imageId, ...spaceOwnerQueryParams(ownerUserId) });
  const response = await authFetch(`${API_BASE_URL}/deleteuserlotimage?${params}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/**
 * @param {string} userLotId - UUID de la unidad funcional
 * @param {{ ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (espacio ajeno, F1).
 */
export const getUserLotImages = async (userLotId, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    user_lot_id: userLotId,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(
    `${API_BASE_URL}/getuserlotimages?${params}`,
    { headers: getHeaders() }
  );
  return parseJsonResponse(response);
};

/** Marca una foto de UF como portada (`is_cover` en DB). */
/**
 * @param {string} imageId
 * @param {string} userLotId
 * @param {{ ownerUserId?: string|null }} [options] `&owner_user_id=` (UF creada por el invitado, mig 082)
 */
export const setUserLotCoverImage = async (imageId, userLotId, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    image_id: imageId,
    user_lot_id: userLotId,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(`${API_BASE_URL}/setuserlotimagecover?${params}`, {
    method: 'PUT',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/**
 * Lista todas las unidades del usuario para un lote catastral.
 * @param {string} lotId - UUID catastral del lote
 * @param {{ ownerUserId?: string|null, includeCommercialSummary?: boolean }} [options]
 *        `ownerUserId` → `?owner_user_id=` (UF del owner en espacio ajeno, F1).
 *        `includeCommercialSummary` → `include=commercial_summary` (solo dueño / editor).
 */
export const getUserLotUnits = async (lotId, { ownerUserId, includeCommercialSummary = false } = {}) => {
  const params = new URLSearchParams({
    lot_id: lotId,
    ...spaceOwnerQueryParams(ownerUserId),
    // `data[].commercial_summary` + raíz `lot_commercial_summary` / `commercial_rollup`.
    ...(includeCommercialSummary ? { include: 'commercial_summary' } : {}),
  });
  const response = await authFetch(`${API_BASE_URL}/getuserlotunits?${params}`, {
    headers: getHeaders(),
  });
  const json = await response.json();
  if (!includeCommercialSummary || !json || typeof json !== 'object') return json;
  const next = { ...json };
  if (Array.isArray(json.data)) {
    next.data = json.data.map((u) => (u && 'commercial_summary' in u
      ? { ...u, commercial_summary: normalizeCommercialSummary(u.commercial_summary) }
      : u));
  }
  if ('lot_commercial_summary' in json) next.lot_commercial_summary = normalizeCommercialSummary(json.lot_commercial_summary);
  if ('commercial_rollup' in json) next.commercial_rollup = normalizeCommercialRollup(json.commercial_rollup);
  return next;
};

/**
 * Agrega un video de YouTube a una UF (máx 4).
 * @param {string} userLotId - UUID de la unidad funcional
 * @param {string} youtubeUrl - URL de YouTube (watch, shorts o youtu.be)
 * @param {{ ownerUserId?: string|null }} [options] `owner_user_id` en el body (F2)
 * @returns {Promise<object>} Envelope httpClient; el video creado viene en `data`.
 */
export const addUserLotVideo = (userLotId, youtubeUrl, { ownerUserId } = {}) => post('/adduserlotvideo', {
  body: {
    user_lot_id: userLotId,
    youtube_url: youtubeUrl,
    ...spaceOwnerQueryParams(ownerUserId),
  },
});

/**
 * Lista videos de YouTube de una UF.
 * @param {string} userLotId - UUID de la unidad funcional
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (espacio ajeno, F1).
 */
export const getUserLotVideos = (userLotId, { signal, ownerUserId } = {}) => get('/getuserlotvideos', {
  params: { user_lot_id: userLotId, ...spaceOwnerQueryParams(ownerUserId) },
  signal,
});

/**
 * Elimina un video de YouTube de una UF.
 * @param {string} videoId - UUID del video
 * @returns {Promise<object>} Envelope; `data.videos` trae los restantes compactados.
 */
/** `ownerUserId` → `&owner_user_id=` (UF creada por el invitado, mig 082). */
export const deleteUserLotVideo = (videoId, { ownerUserId } = {}) => del('/deleteuserlotvideo', {
  params: { video_id: videoId, ...spaceOwnerQueryParams(ownerUserId) },
});

/**
 * GET /getuserlotattachmentpresignedurl — ContentType + ContentLength firmados.
 * @param {string} userLotId
 * @param {{ contentType: string, contentLength: number }} params
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (F2; mismo owner en el save)
 */
export const getUserLotAttachmentPresignedUrl = (
  userLotId,
  { contentType, contentLength } = {},
  { signal, ownerUserId } = {},
) => get('/getuserlotattachmentpresignedurl', {
  params: {
    user_lot_id: userLotId,
    contentType,
    contentLength,
    ...spaceOwnerQueryParams(ownerUserId),
  },
  signal,
});

/**
 * PUT directo a S3 con el mismo Content-Type (y tamaño) firmados.
 * fetch: httpClient no sirve para URLs prefirmadas externas.
 * @param {{ presignedUrl: string, file: File|Blob, contentType: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function putUserLotAttachmentToS3(
  { presignedUrl, file, contentType } = {},
  { signal } = {},
) {
  if (!presignedUrl || !file || !contentType) {
    return {
      ok: false,
      message: 'Falta la URL de carga o el archivo.',
      _httpStatus: null,
    };
  }

  const UPLOAD_TIMEOUT_MS = 120000;
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onExternalAbort);
  }
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, UPLOAD_TIMEOUT_MS);

  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
      signal: controller.signal,
    });
    if (response.ok) {
      return { ok: true, _httpStatus: response.status };
    }
    return {
      ok: false,
      message: 'No se pudo subir el archivo. Probá de nuevo.',
      _httpStatus: response.status,
    };
  } catch (error) {
    if (timedOut) {
      return {
        ok: false,
        message: 'La subida tardó demasiado. Probá con una conexión más estable.',
        _httpStatus: null,
      };
    }
    if (error?.name === 'AbortError') throw error;
    console.error('UF attachment: PUT a S3 falló', error);
    return {
      ok: false,
      message: 'No se pudo subir el archivo.',
      _httpStatus: null,
    };
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
}

const USER_LOT_ATTACHMENT_FILE_NAME_MAX_LENGTH = 200;

/**
 * Normaliza el nombre original antes de enviarlo al API. File.name ya suele ser
 * basename, pero el split también cubre archivos construidos con una ruta.
 * @param {unknown} fileName
 * @returns {string|null}
 */
export const normalizeUserLotAttachmentFileName = (fileName) => {
  const name = String(fileName ?? '').trim();
  if (!name) return null;
  const basename = name.split(/[\\/]/).filter(Boolean).pop() || '';
  return basename.slice(0, USER_LOT_ATTACHMENT_FILE_NAME_MAX_LENGTH) || null;
};

/**
 * POST /saveuserlotattachment — cita s3_key + content_type tras el PUT.
 * @param {string} userLotId
 * @param {{ s3Key: string, contentType: string, fileName?: string }} params
 * @param {{ ownerUserId?: string|null }} [options] `owner_user_id` en el body (F2)
 */
export const saveUserLotAttachment = (
  userLotId,
  { s3Key, contentType, fileName, file_name: fileNameSnake, original_filename: originalFilename } = {},
  { ownerUserId } = {},
) => post('/saveuserlotattachment', {
  body: {
    user_lot_id: userLotId,
    s3_key: s3Key,
    content_type: contentType,
    file_name: normalizeUserLotAttachmentFileName(fileName ?? fileNameSnake ?? originalFilename),
    ...spaceOwnerQueryParams(ownerUserId),
  },
});

/**
 * GET /getuserlotattachments?user_lot_id=
 * @param {string} userLotId
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (espacio ajeno, F1).
 */
export const getUserLotAttachments = (userLotId, { signal, ownerUserId } = {}) => get('/getuserlotattachments', {
  params: { user_lot_id: userLotId, ...spaceOwnerQueryParams(ownerUserId) },
  signal,
});

/**
 * DELETE /deleteuserlotattachment?attachment_id=
 * @param {string} attachmentId
 * @returns {Promise<object>} Envelope; `data.attachments` trae los restantes compactados.
 */
/** `ownerUserId` → `&owner_user_id=` (UF creada por el invitado, mig 082). */
export const deleteUserLotAttachment = (attachmentId, { ownerUserId } = {}) => del('/deleteuserlotattachment', {
  params: { attachment_id: attachmentId, ...spaceOwnerQueryParams(ownerUserId) },
});

/**
 * Presign → PUT S3 → save. Devuelve el adjunto guardado (`data` del save).
 * @param {string} userLotId
 * @param {File|Blob} file
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null, validate?: (file: File|Blob) => { ok: boolean, contentType?: string, message?: string } }} [options]
 *        `ownerUserId`: el mismo `owner_user_id` en presign y save (F2).
 */
export async function uploadUserLotAttachment(userLotId, file, { signal, validate, ownerUserId } = {}) {
  const validation = typeof validate === 'function'
    ? validate(file)
    : { ok: Boolean(file?.type && file?.size), contentType: file?.type, message: 'Archivo inválido.' };
  if (!validation.ok) {
    throw new Error(validation.message || 'Archivo inválido.');
  }

  const contentType = validation.contentType || file.type;
  const contentLength = Number(file.size);

  const presigned = await getUserLotAttachmentPresignedUrl(
    userLotId,
    { contentType, contentLength },
    { signal, ownerUserId },
  );

  if (!presigned._httpOk || !presigned.data?.presignedUrl || !presigned.data?.s3Key) {
    const err = new Error(
      presigned?.message || presigned?.error || 'No se pudo preparar la subida del adjunto.',
    );
    err.apiResponse = presigned;
    throw err;
  }

  const {
    presignedUrl,
    s3Key,
    contentType: signedContentType,
  } = presigned.data;

  const putResult = await putUserLotAttachmentToS3(
    {
      presignedUrl,
      file,
      contentType: signedContentType || contentType,
    },
    { signal },
  );

  if (!putResult.ok) {
    throw new Error(putResult.message || 'No se pudo subir el archivo.');
  }

  const saveRes = await saveUserLotAttachment(userLotId, {
    s3Key,
    contentType: signedContentType || contentType,
    fileName: file.name,
  }, { ownerUserId });

  if (!saveRes._httpOk || !saveRes.data) {
    const err = new Error(
      saveRes?.message || saveRes?.error || 'No se pudo guardar el adjunto.',
    );
    err.apiResponse = saveRes;
    throw err;
  }

  return saveRes;
}
